import { useScroll, useTransform } from 'framer-motion'
import { useEffect, useRef, useState, type FC, type JSX } from 'react'
import type { CSSProperties } from 'styled-components'

import { IconArrow } from 'components/icons/arrow'

import {
  Description,
  NavButton,
  Page,
  Pagination,
  Scroll,
  ScrollText,
  SnapPoint,
  SnapPoints,
  Title,
} from './styled'

type TimelineEntry = {
  index: number
  isActive: boolean
  year: number
  label: string
  description: JSX.Element
  element?: Element | null
}

type Props = {
  readonly entryIndex: { current: number; previous: number }
  readonly onChangeEntry: (element?: Element | null) => void
  readonly activeEntry: TimelineEntry
  readonly nextEntry: TimelineEntry | null
  readonly previousEntry: TimelineEntry | null
}

const Inner = ({ activeEntry }: Pick<Props, 'activeEntry' | 'entryIndex'>) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [containerSize, setContainerSize] = useState({
    scrollWidth: 0,
    clientWidth: 0,
  })
  const [activeIndex, setActiveIndex] = useState(0)
  const totalPages = Math.round(
    containerSize.scrollWidth / containerSize.clientWidth
  )

  const resizeObserver = useRef(
    new ResizeObserver(([entry]) => {
      setContainerSize({
        scrollWidth: entry.target.scrollWidth,
        clientWidth: entry.target.clientWidth,
      })
    })
  )

  const { scrollXProgress } = useScroll({ container: scrollRef, axis: 'x' })
  const scrollIndex = useTransform(scrollXProgress, value =>
    Math.round(
      (containerSize.scrollWidth / containerSize.clientWidth - 1) * value
    )
  )

  useEffect(() => {
    scrollIndex.on('change', setActiveIndex)

    resizeObserver.current = new ResizeObserver(([entry]) => {
      setContainerSize({
        scrollWidth: entry.target.scrollWidth,
        clientWidth: entry.target.clientWidth,
      })
    })

    if (scrollRef.current) {
      resizeObserver.current.observe(scrollRef.current)
      setContainerSize({
        scrollWidth: scrollRef.current.scrollWidth,
        clientWidth: scrollRef.current.clientWidth,
      })
    }

    return () => {
      resizeObserver.current.disconnect()
    }
  }, [activeEntry])

  return (
    <>
      <Scroll ref={scrollRef} key={activeEntry.index.toString()}>
        <Title>{activeEntry.label}</Title>
        <ScrollText>{activeEntry.description}</ScrollText>

        {!isNaN(totalPages) && totalPages > 1 && (
          <SnapPoints
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            style={{ '--number-of-pages': totalPages } as CSSProperties}
          >
            {[...(Array(totalPages) as number[])]
              .map((_, i) => i)
              .map(index => (
                <SnapPoint key={index} />
              ))}
          </SnapPoints>
        )}
      </Scroll>

      {!isNaN(totalPages) && totalPages > 1 && (
        <Pagination>
          {[...(Array(totalPages) as number[])]
            .map((_, i) => i)
            .map(index => (
              <Page
                key={index}
                $isActive={index === activeIndex}
                aria-label={`Page ${index + 1}`}
              />
            ))}
        </Pagination>
      )}
    </>
  )
}

export const TimelineDescription: FC<Props> = ({
  entryIndex,
  onChangeEntry,
  activeEntry,
  nextEntry,
  previousEntry,
}) => {
  return (
    <Description>
      {activeEntry && (
        <Inner
          activeEntry={activeEntry}
          entryIndex={entryIndex}
          key={activeEntry.index.toString()}
        />
      )}

      <NavButton
        disabled={!previousEntry}
        type="button"
        onClick={() => onChangeEntry(previousEntry?.element)}
        aria-label="Previous"
      >
        <IconArrow />
      </NavButton>
      <NavButton
        disabled={!nextEntry}
        type="button"
        onClick={() => onChangeEntry(nextEntry?.element)}
        aria-label="Next"
        $align="right"
      >
        <IconArrow />
      </NavButton>
    </Description>
  )
}
